import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google'
import { useAuth } from '../../hooks/useAuth'
import LandingHeader from '../../components/Landing/sections/LandingHeader.jsx'
import { ForgotPasswordModal, CheckEmailModal } from '../../components/auth/ForgotPasswordModal'
import { useTranslation } from 'react-i18next';
import { USER_TYPES } from '../../constants/userTypes'
import { authAPI } from '../../services/api'


const Login = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const currentLanguage = i18n.language;
  const handleLanguageToggle = () => {
    const newLang = currentLanguage === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { oauthLogin, isAuthenticated, isLoading: authLoading } = useAuth()

  // User type based color accents (guardian, vet, etc.)
  const { userTypeColor, userTypeHoverColor } = useMemo(() => {
    const normalizeToKey = (rawType) => {
      if (!rawType) return null
      const val = String(rawType).toLowerCase()
      if (['pet_guardian', 'pet-guardian', 'guardian'].includes(val)) return 'PET_GUARDIAN'
      if (['veterinarian', 'vet'].includes(val)) return 'VETERINARIAN'
      if (['SERVICE_PROVIDER', 'pet-shop-owner', 'shop_owner', 'shop-owner'].includes(val)) return 'SERVICE_PROVIDER'
      if (['service_provider', 'service-provider'].includes(val)) return 'SERVICE_PROVIDER'
      if (['clinic_moderator', 'clinic-moderator'].includes(val)) return 'CLINIC_MODERATOR'
      return null
    }

    const getBaseColor = (key) => {
      switch (key) {
        case 'VETERINARIAN':
          return '#00D4B3'
        case 'SERVICE_PROVIDER':
          return '#D65745'
        case 'CLINIC_MODERATOR':
          return '#2A8AD2'
        case 'PET_GUARDIAN':
        default:
          return '#8548A8'
      }
    }

    const darkenColor = (hex, percent) => {
      const num = parseInt(hex.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent)
      const R = Math.max(0, Math.min(255, (num >> 16) + amt))
      const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt))
      const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt))
      return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`
    }

    // 1) From URL ?userType=pet_guardian / veterinarian / etc.
    const urlType = searchParams.get('userType')
    let key = normalizeToKey(urlType)

    // 2) Fallback to last known user_data in localStorage
    if (!key) {
      try {
        const stored = localStorage.getItem('user_data')
        if (stored) {
          const parsed = JSON.parse(stored)
          key =
            normalizeToKey(parsed.user_type || parsed.userType || parsed.type) ||
            null
        }
      } catch {
        key = null
      }
    }

    if (!key) key = 'PET_GUARDIAN'

    const base = getBaseColor(key)
    const hover = darkenColor(base, -15)
    return { userTypeColor: base, userTypeHoverColor: hover }
  }, [searchParams])

  // DEMO MODE FLAG - Set this to true to enable demo mode
  const DEMO_MODE = false // Change this to false to enable real authentication

  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    password: '',
    rememberMe: false
  })
  const [activeTab, setActiveTab] = useState('email') // 'email' or 'mobile'
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [twoFARequired, setTwoFARequired] = useState(false)
  const [twoFACode, setTwoFACode] = useState('')
  const [tempToken, setTempToken] = useState('')

  // Forgot Password States
  const [showForgotModal, setShowForgotModal] = useState(false)
  const [showCheckEmailModal, setShowCheckEmailModal] = useState(false)
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [forgotPasswordError, setForgotPasswordError] = useState('')
  const [isResendingEmail, setIsResendingEmail] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetIdentifierType, setResetIdentifierType] = useState('email')

  // Redirect if user is already logged in
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // Check user type from localStorage
      const userDataStr = localStorage.getItem('user_data')
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr)
          const userType = userData.user_type || userData.userType || userData.type

          // ADMIN users always go to admin dashboard
          if (userType === USER_TYPES.ADMIN) {
            navigate('/admin/dashboard', { replace: true })
            return
          }

          // CLINIC_MODERATOR users go to appointments
          if (userType === USER_TYPES.CLINIC_MODERATOR) {
            navigate('/medical/clinic/appointment-management/appointments', { replace: true })
            return
          }

          // DEV_ADMIN users go to dev admin page
          if (userType === USER_TYPES.DEV_ADMIN) {
            navigate('/dev-admin', { replace: true })
            return
          }

          // CONTENT_ADMIN users go to CMS dashboard
          if (userType === USER_TYPES.CONTENT_ADMIN) {
            navigate('/admin/dashboard/cms/banners', { replace: true })
            return
          }
        } catch (error) {
          console.error('Error parsing user data:', error)
        }
      }

      // Get the intended route or default to social feed
      const intendedRoute = localStorage.getItem('intended_route') || '/social'
      localStorage.removeItem('intended_route')
      navigate(intendedRoute, { replace: true })
    }
  }, [isAuthenticated, authLoading, navigate])

  // Google OAuth handlers
  const handleGoogleSuccess = useCallback(async (credentialResponse) => {
    try {
      setIsLoading(true)
      setErrors({})


      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      // Send the credential to your backend
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/google/verify-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: credentialResponse.access_token
        }),
      })

      const data = await response.json()

      if (response.ok) {
        // Handle the response based on the actual backend structure
        const responseData = data.data || data

        if (responseData.user) {
          // Store user data and token
          localStorage.setItem('access_token', responseData.access_token)
          localStorage.setItem('refresh_token', responseData.refresh_token)
          localStorage.setItem('user_data', JSON.stringify(responseData.user))

          // Authenticate the user in the auth context
          try {
            await oauthLogin(responseData.user, responseData.access_token)
          } catch (error) {
            console.error('Error authenticating user:', error)
          }

          // Get user type from response
          const userType = responseData.user.user_type || responseData.user.userType || responseData.user.type

          if (responseData.is_new_user) {
            // New user - redirect to Google registration
            navigate(`/auth/google-register/${userType}`, {
              state: {
                googleData: responseData.user,
                access_token: responseData.access_token
              }
            })
          } else {
            // Check if user needs profile completion
            if (responseData.user.profile_status == "incomplete") {
              // Redirect to profile completion
              navigate(`/auth/complete-profile/${userType}`, {
                state: {
                  user: responseData.user,
                  access_token: responseData.access_token,
                  loginMethod: 'google'
                }
              })
            } else {
              // Existing user with complete profile - navigate to dashboard
              let redirectRoute = '/social'

              if (userType === USER_TYPES.ADMIN) {
                redirectRoute = '/admin/dashboard'
              } else if (userType === USER_TYPES.CONTENT_ADMIN) {
                redirectRoute = '/admin/dashboard/cms/banners'
              } else if (userType === USER_TYPES.CLINIC_MODERATOR) {
                redirectRoute = '/medical/clinic/appointment-management/appointments'
              } else {
                redirectRoute = localStorage.getItem('google_login_intended_route') || '/social'
              }

              localStorage.removeItem('google_login_intended_route')

              navigate(redirectRoute, {
                state: {
                  loginSuccess: true,
                  user: responseData.user,
                  loginMethod: 'google'
                }
              })
            }
          }

        } else {
          setErrors({ general: data.message || 'Google login failed. Please try again.' })
        }
      } else {
        // Handle error response structure
        if (data.errors?.message) {
          setErrors({ general: data.errors.message })
        } else if (data.message) {
          setErrors({ general: data.message })
        } else {
          setErrors({ general: t('login.errors.googleFailed') })
        }
      }
    } catch (error) {
      console.error('Google OAuth error:', error)

      // Handle specific error response structure
      if (error.response?.data) {
        const errorData = error.response.data

        // Show the actual error message from the response
        if (errorData.errors?.message) {
          setErrors({ general: errorData.errors.message })
        } else if (errorData.message) {
          setErrors({ general: errorData.message })
        } else {
          setErrors({ general: t('login.errors.googleFailed') })
        }
      } else {
        setErrors({ general: t('login.errors.googleFailed') })
      }
    } finally {
      setIsLoading(false)
    }
  }, [navigate, oauthLogin, t])

  // Handle Google OAuth error
  const handleGoogleError = useCallback(() => {
    console.error('Google OAuth failed')
    setErrors({ general: t('login.errors.googleFailed') })
    setIsLoading(false)
  }, [t])

  // Google OAuth hook - defined after the handler functions
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  })

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#FAFAFA',
        position: 'fixed',
        top: 0,
        left: 0,
        fontFamily: '"Red Hat Display", sans-serif',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #E5E7EB',
            borderTop: '4px solid #1976D2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</p>
        </div>
      </div>
    )
  }

  // API Configuration
  const API_BASE_URL = import.meta.env.PROD
    ? (import.meta.env.VITE_API_URL || 'http://localhost:8000')
    : '/api/v1'

  // Demo Mode Mock Data
  const createMockUserData = () => {
    return {
      user_id: 'demo_user_123',
      email: formData.email || 'demo@sulhafa.com',
      first_name: 'Demo',
      last_name: 'User',
      user_type: 'pet-guardian', // or 'veterinarian', 'pet-shop-owner'
      account_status: 'active',
      profile_picture: null,
      phone: '+1234567890',
      is_verified: true,
      is_2fa_enabled: false,
      created_at: new Date().toISOString(),
      last_login: new Date().toISOString(),
      onboarding_completed: true,
      preferences: {
        notifications: true,
        marketing_emails: false,
        theme: 'light'
      }
    }
  }

  const createMockTokens = () => {
    // Create mock JWT-like tokens for demo
    const mockAccessToken = `demo_access_token_${Date.now()}`
    const mockRefreshToken = `demo_refresh_token_${Date.now()}`

    return {
      access_token: mockAccessToken,
      refresh_token: mockRefreshToken,
      token_type: 'Bearer',
      expires_in: 3600
    }
  }


  // Enhanced API client with error handling (only used when DEMO_MODE is false)
  const apiClient = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      })

      const data = await response.json()

      if (!response.ok) {
        throw { type: data.errors.type, message: data.message, errors: data.errors }
      }

      return data
    } catch (error) {
      throw error
    }
  }

  // Get device fingerprint for security
  const getDeviceInfo = () => {
    return {
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      device_id: localStorage.getItem('device_id') || generateDeviceId()
    }
  }

  const generateDeviceId = () => {
    const deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
    localStorage.setItem('device_id', deviceId)
    return deviceId
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validation function
  const validateForm = () => {
    const newErrors = {}

    // In demo mode, skip validation if fields are empty
    if (DEMO_MODE) {
      return true // Always valid in demo mode
    }

    // Email/Phone validation
    if (activeTab === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = t('login.errors.emailRequired')
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.email)) {
          newErrors.email = t('login.errors.emailInvalid')
        }
      }
    } else {
      if (!formData.mobile.trim()) {
        newErrors.mobile = t('login.errors.mobileRequired')
      } else {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
        if (!phoneRegex.test(formData.mobile.replace(/\s+/g, ''))) {
          newErrors.mobile = t('login.errors.mobileInvalid')
        }
      }
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = t('login.errors.passwordRequired')
    } else if (formData.password.length < 6) {
      newErrors.password = t('login.errors.passwordTooShort')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Clear any previous errors
    setErrors({})


    // Real authentication flow (only when DEMO_MODE is false)
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const deviceInfo = getDeviceInfo()

      const loginPayload = {
        identifier: activeTab == "email" ? formData.email.trim() : formData.mobile.trim(), // Can be email or mobile based on activeTab
        identifier_type: activeTab,// 'email' or 'mobile'
        password: formData.password,
        remember_me: formData.rememberMe,
        device_info: deviceInfo
      }
      const response = await authAPI.login(loginPayload)
      const responseData = response.data

      if (responseData.data.requires_2fa) {
        // Handle 2FA requirement
        setTwoFARequired(true)
        setTempToken(responseData.access_token)
        setErrors({ general: t('login.errors.twoFARequired') })
      } else {
        // Successful login
        await handleSuccessfulLogin(responseData)
      }

    } catch (error) {
      console.error('Login failed:', error)

      // Handle specific error response structure
      if (error.response?.data) {
        const errorData = error.response.data

        // Show the actual error message from the response
        if (errorData.errors?.message) {
          setErrors({
            [activeTab]: errorData.errors.message
          })
        } else if (errorData.message) {
          setErrors({
            [activeTab]: errorData.message
          })
        } else {
          setErrors({
            general: t('login.errors.loginFailed')
          })
        }
      } else {
        // Handle specific error cases
        if (error.type === 'invalid_credentials') {
          setErrors({
            [activeTab]: error.message
          })
        } else if (error.type === 'account_suspended') {
          setErrors({
            general: error.message || t('login.errors.accountSuspended')
          })
        } else if (error === 'too_many_attempts') {
          setErrors({
            [activeTab]: t('login.errors.tooManyAttempts')
          })
        } else if (error === 'account_locked') {
          setErrors({
            [activeTab]: t('login.errors.accountLocked')
          })
        } else {
          setErrors({
            general: t('login.errors.loginFailed')
          })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handle2FASubmit = async (e) => {
    e.preventDefault()

    if (!twoFACode.trim()) {
      setErrors({ twoFA: t('login.errors.twoFACodeRequired') })
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const response = await apiClient('/auth/2fa/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({
          code: twoFACode.trim(),
          device_info: getDeviceInfo()
        })
      })

      await handleSuccessfulLogin(response.data)

    } catch (error) {
      console.error('2FA verification failed:', error)

      // Handle specific error response structure
      if (error.response?.data) {
        const errorData = error.response.data

        // Show the actual error message from the response
        if (errorData.errors?.message) {
          setErrors({
            twoFA: errorData.errors.message
          })
        } else if (errorData.message) {
          setErrors({
            twoFA: errorData.message
          })
        } else {
          setErrors({
            twoFA: t('login.errors.twoFAVerifyFailed')
          })
        }
      } else {
        setErrors({
          twoFA: error.message && error.message.includes('Invalid')
            ? t('login.errors.twoFAInvalid')
            : t('login.errors.twoFAVerifyFailed')
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessfulLogin = async (response) => {
    let userData = response.data;
    try {
      // Store tokens securely
      localStorage.setItem('access_token', userData.access_token)
      if (userData.refresh_token) {
        localStorage.setItem('refresh_token', userData.refresh_token)
      }
      // Store user data
      if (userData.user) {
        localStorage.setItem('user_data', JSON.stringify(userData.user))
      }

      // Update last login
      localStorage.setItem('last_login', new Date().toISOString())


      // Navigate to dashboard or intended page
      localStorage.setItem('auth_loading', 'false');
      localStorage.setItem('isAuthenticated', 'true');

      const userType = userData.user.user_type || userData.user.userType || userData.user.type

      // Check profile completion (except for CLINIC_MODERATOR and ADMIN)
      if (userType !== USER_TYPES.CLINIC_MODERATOR && userType !== USER_TYPES.ADMIN && userData.user.profile_status == "incomplete") {
        navigate('/auth/complete-profile/' + userType);
        return;
      }

      // ADMIN users always go to admin dashboard (ignore intended_route)
      if (userType === USER_TYPES.ADMIN) {
        localStorage.removeItem('intended_route');
        location.href = '/admin/dashboard';
        return;
      }

      // CLINIC_MODERATOR users go to appointments
      if (userType === USER_TYPES.CLINIC_MODERATOR) {
        localStorage.removeItem('intended_route');
        location.href = '/medical/clinic/appointment-management/appointments';
        return;
      }

      // DEV_ADMIN users go to dev admin page
      if (userType === USER_TYPES.DEV_ADMIN) {
        localStorage.removeItem('intended_route');
        location.href = '/dev-admin';
        return;
      }

      // CONTENT_ADMIN users go to CMS dashboard
      if (userType === USER_TYPES.CONTENT_ADMIN) {
        localStorage.removeItem('intended_route');
        location.href = '/admin/dashboard/cms/banners';
        return;
      }

      // FIRM users always land on Connect (Sulhafa Connect / social feed)
      if (userType === USER_TYPES.FIRM || userType === 'FIRM' || userType === 'firm') {
        localStorage.removeItem('intended_route');
        location.href = '/social';
        return;
      }

      // Other users go to intended route or default
      const intendedRoute = localStorage.getItem('intended_route') || '/social'
      localStorage.removeItem('intended_route');
      location.href = intendedRoute;

    } catch (error) {
      console.error('Error handling successful login:', error)
      setErrors({ general: t('login.errors.postLogin') })
    }
  }


  const handleAppleLogin = async () => {
    if (DEMO_MODE) {
      setFormData(prev => ({ ...prev, email: 'demo.apple@sulhafa.com' }))
      // await handleDemoLogin()
      return
    }

    try {
      setIsLoading(true)
      const response = await authAPI.appleRedirect()
      const data = response.data.data || response.data

      if (data.auth_url) {
        // Store intended route and user type for callback
        const urlType = searchParams.get('userType') || 'pet_guardian'
        localStorage.setItem('apple_login_user_type', urlType)
        localStorage.setItem('apple_login_intended_route', localStorage.getItem('intended_route') || '/social')

        // Redirect to Apple
        window.location.href = data.auth_url
      } else {
        setErrors({ general: 'Failed to initiate Apple login. Please try again.' })
      }
    } catch (error) {
      console.error('Apple login initiation failed:', error)
      setErrors({ general: t('login.errors.appleFailed', 'Apple login failed. Please try again.') })
    } finally {
      setIsLoading(false)
    }
  }

  // Forgot Password Handler 
  const handleForgotPasswordSubmit = async (identifier, identifierType) => {
    if (DEMO_MODE) {
      // In demo mode, simulate successful password reset request
      setResetEmail(identifier)
      setResetIdentifierType(identifierType || 'email')
      setShowForgotModal(false)
      setShowCheckEmailModal(true)
      return { success: true }
    }

    setForgotPasswordLoading(true)
    setForgotPasswordError('')

    try {
      // Send request with identifier field (backend now supports both email and phone)
      const requestData = { identifier: identifier.trim() }

      console.log('Sending password reset request:', {
        identifier: identifier.trim(),
        identifierType,
        requestData
      })

      const response = await authAPI.sendPasswordResetLink(requestData)

      console.log('Password reset response:', response)

      // Success - store identifier and type, then show success modal
      setResetEmail(identifier)
      setResetIdentifierType(identifierType || 'email')
      setShowForgotModal(false)
      setShowCheckEmailModal(true)

      return { success: true }

    } catch (error) {
      console.error('Password reset request failed:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      })

      // Handle specific error response structure
      if (error.response?.data) {
        const errorData = error.response.data

        // Check if it's a validation error with multiple fields
        if (errorData.errors && typeof errorData.errors === 'object') {
          // Get the first error message from the errors object
          const firstErrorKey = Object.keys(errorData.errors)[0]
          const firstError = Array.isArray(errorData.errors[firstErrorKey])
            ? errorData.errors[firstErrorKey][0]
            : errorData.errors[firstErrorKey]

          if (firstError) {
            setForgotPasswordError(firstError)
          } else if (errorData.message) {
            setForgotPasswordError(errorData.message)
          } else {
            setForgotPasswordError(t('login.errors.resetFailed'))
          }
        } else if (errorData.errors?.message) {
          setForgotPasswordError(errorData.errors.message)
        } else if (errorData.message) {
          setForgotPasswordError(errorData.message)
        } else {
          setForgotPasswordError(t('login.errors.resetFailed'))
        }
      } else if (error.errors?.message) {
        setForgotPasswordError(error.errors.message)
      } else if (error.message) {
        setForgotPasswordError(error.message)
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        // Network error or no response
        setForgotPasswordError('Network error. Please check your connection and try again.')
      } else {
        setForgotPasswordError(t('login.errors.resetFailed'))
      }

      return { success: false }
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  // Demo mode banner component - removed for clean design
  const DemoModeBanner = () => {
    return null // No visual indicators
  }

  // If 2FA is required, show 2FA form (only in real auth mode)
  if (twoFARequired && !DEMO_MODE) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#FAFAFA',
        position: 'fixed',
        top: 0,
        left: 0,
        fontFamily: '"Red Hat Display", sans-serif',
        overflow: 'hidden',
        display: 'flex'
      }}>

        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 20 }}>
          <LandingHeader hideLoginButton />
        </div>

        {/* 2FA Form Implementation */}
        {/* ... (same as your original 2FA form) */}
      </div>
    )
  }

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#FAFAFA',
      position: 'fixed',
      top: 0,
      left: 0,
      fontFamily: '"Red Hat Display", sans-serif',
      overflow: 'auto',
      display: 'flex'
    }}>
      {/* Demo Mode Banner */}
      <DemoModeBanner />

      {/* Header Component */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 20 }}>
        <LandingHeader hideLoginButton />
      </div>

      {/* Left Section - Design Panel */}
      <div className="login-mascot-section" style={{
        width: '50%',
        height: '100vh',
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '60px 24px 24px 24px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Title Section */}
        <div style={{
          position: 'absolute',
          top: '120px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          zIndex: 3,
          width: '100%',
          padding: '0 20px',
          boxSizing: 'border-box'
        }}>
          <h2 style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            fontSize: 'clamp(14px, 2vw, 20px)',
            lineHeight: '1.2',
            color: '#303E4E',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em'
          }}>
            {t('login.caring')}
          </h2>
        </div>

        {/* Clean Sulhafa Character Display */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '400px',
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          <img
            src="/images/Sulhafa-Main.png"
            alt="Sulhafa Mascot"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transition: 'transform 0.3s ease',
              transform: isRTL ? 'scale(1.5) scaleX(1)' : 'scale(1.5) scaleX(-1)',
              filter: 'drop-shadow(0 10px 25px rgba(0, 0, 0, 0.1))'
            }}
            onError={(e) => {
              e.target.src = "/public/character/turtle-character.jpeg"
            }}
          />
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="login-form-section" style={{
        width: '50%',
        height: '100vh',
        background: '#FFFFFF',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        padding: '60px 48px 48px 48px'
      }}>
        <div className="login-form-container" style={{
          width: '100%',
          maxWidth: '400px' // reduced max width
        }}>
          {/* Form Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '28px',
              fontWeight: 700,
              color: '#303E4E',
              marginBottom: '0',
              lineHeight: '1.3'
            }}>
              {t('login.title')}
            </h3>
          </div>

          {/* Error Message */}
          {errors.general && (
            <div style={{
              padding: '12px 16px',
              background: errors.general.includes('sent') ? '#F0F9FF' : '#FEF2F2',
              border: `1px solid ${errors.general.includes('sent') ? '#BAE6FD' : '#FECACA'}`,
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: errors.general.includes('sent') ? '#0284C7' : '#EF4444',
                margin: '0'
              }}>
                {errors.general}
              </p>
            </div>
          )}

          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            marginBottom: '20px',
            borderBottom: '2px solid #F3F4F6'
          }}>
            <button
              type="button"
              onClick={() => {
                setActiveTab('email')
                // Clear errors when switching tabs
                setErrors(prev => {
                  const newErrors = { ...prev }
                  delete newErrors.mobile
                  return newErrors
                })
              }}
              style={{
                flex: 1,
                padding: '10px',
                background: 'transparent',
                border: 'none',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                color: activeTab === 'email' ? userTypeColor : '#9CA3AF',
                borderBottom: activeTab === 'email' ? `3px solid ${userTypeColor}` : '3px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('login.tabs.email')}
            </button>
            {/* Mobile login temporarily disabled
            <button
              type="button"
              onClick={() => {
                setActiveTab('mobile')
                // Clear errors when switching tabs
                setErrors(prev => {
                  const newErrors = { ...prev }
                  delete newErrors.email
                  return newErrors
                })
              }}
              style={{
                flex: 1,
                padding: '10px',
                background: 'transparent',
                border: 'none',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                color: activeTab === 'mobile' ? userTypeColor : '#9CA3AF',
                borderBottom: activeTab === 'mobile' ? `3px solid ${userTypeColor}` : '3px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('login.tabs.mobile')}
            </button>
            */}
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email Input - Mobile login temporarily disabled */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  position: 'absolute',
                  left: isRTL ? 'auto' : '20px',
                  right: isRTL ? '20px' : 'auto',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 20" fill="none">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="22,6 12,13 2,6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('login.emailPlaceholder')}
                  required
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    height: '50px',
                    padding: isRTL ? '0 50px 0 12px' : '0 12px 0 50px',
                    border: `1.5px solid ${errors.email ? '#EF4444' : userTypeColor + '66'}`,
                    borderRadius: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '18px',
                    color: '#1F2937',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box',
                    background: isLoading ? '#F9FAFB' : '#FFFFFF',
                    cursor: isLoading ? 'not-allowed' : 'text',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                  onFocus={(e) => {
                    if (!isLoading) e.target.style.borderColor = userTypeColor
                  }}
                  onBlur={(e) => {
                    if (!isLoading) {
                      e.target.style.borderColor = errors.email ? '#EF4444' : userTypeColor + '66'
                    }
                  }}
                />
              </div>
              {errors.email && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '10px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  position: 'absolute',
                  left: isRTL ? 'auto' : '20px',
                  right: isRTL ? '20px' : 'auto',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2" />
                    <circle cx="12" cy="16" r="1" fill="#9CA3AF" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    height: '50px',
                    padding: isRTL ? '0 50px 0 12px' : '0 12px 0 50px',
                    border: `1.5px solid ${errors.password ? '#EF4444' : userTypeColor + '66'}`,
                    borderRadius: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '16px',
                    color: '#1F2937',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box',
                    background: isLoading ? '#F9FAFB' : '#FFFFFF',
                    cursor: isLoading ? 'not-allowed' : 'text',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                  onFocus={(e) => {
                    if (!isLoading) e.target.style.borderColor = userTypeColor
                  }}
                  onBlur={(e) => {
                    if (!isLoading) {
                      e.target.style.borderColor = errors.password ? '#EF4444' : userTypeColor + '66'
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    right: isRTL ? 'auto' : '20px',
                    left: isRTL ? '20px' : 'auto',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    zIndex: 2,
                    padding: '4px'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    {showPassword ? (
                      <>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <line x1="1" y1="1" x2="23" y2="23" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </>
                    ) : (
                      <>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="12" r="3" stroke="#9CA3AF" strokeWidth="2" />
                      </>
                    )}
                  </svg>
                </button>
              </div>
              {errors.password && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '10px',
                  color: '#EF4444',
                  margin: '4px 0 0 0'
                }}>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '12px',
                color: '#6B7280',
                opacity: isLoading ? 0.6 : 1
              }}>
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  style={{
                    marginRight: '8px',
                    width: '16px',
                    height: '16px',
                    accentColor: userTypeColor,
                    cursor: isLoading ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('login.rememberMe')}
              </label>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                disabled={isLoading}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#303E4E',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textDecoration: 'none',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                {t('login.forgotPassword')}
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                height: '40px',
                padding: '0 20px',
                background: isLoading ? '#9CA3AF' : userTypeColor,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '14px',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '12px',
                marginLeft: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = userTypeHoverColor
                  e.target.style.transform = 'translateY(-1px)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = userTypeColor
                  e.target.style.transform = 'translateY(0)'
                }
              }}
            >
              {isLoading && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.3" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {isLoading ? t('login.signingIn') : t('login.loginButton')}
            </button>

            {/* Divider */}
            <div style={{
              position: 'relative',
              textAlign: 'center',
              margin: '12px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                flex: 1,
                height: '1px',
                background: '#E5E7EB',
                marginRight: '16px'
              }}></div>
              <span style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '11px',
                color: '#6B7280',
                whiteSpace: 'nowrap'
              }}>
                {t('login.orContinueWith')}
              </span>
              <div style={{
                flex: 1,
                height: '1px',
                background: '#E5E7EB',
                marginLeft: '16px'
              }}></div>
            </div>

            {/* Social Login Buttons */}
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: '#6B7280',
              textAlign: 'center',
              marginBottom: '12px'
            }}>
              {t('login.socialTitle')}
            </p>

            <div style={{
              display: 'flex',
              gap: '16px',
              width: '100%',
              marginBottom: '20px'
            }}>
              {/* Google Button */}
              <button
                type="button"
                onClick={() => googleLogin()}
                disabled={isLoading}
                style={{
                  flex: 1,
                  height: '48px',
                  background: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = '#F9FAFB'
                    e.currentTarget.style.borderColor = '#D1D5DB'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = 'white'
                    e.currentTarget.style.borderColor = '#E5E7EB'
                  }
                }}
              >
                <img
                  src="/icons/google-icon.svg"
                  alt={t('login.social.google')}
                  style={{ width: '20px', height: '20px' }}
                />
                <span style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151'
                }}>
                  {t('login.social.google')}
                </span>
              </button>

              {/* Apple Button */}
              <button
                type="button"
                onClick={handleAppleLogin}
                disabled={isLoading}
                style={{
                  flex: 1,
                  height: '48px',
                  background: 'black',
                  border: 'none',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = '#1a1a1a'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = 'black'
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M17.05 20.28c-.96.95-2.04 1.72-3.23 1.72-1.16 0-1.54-.71-2.94-.71-1.41 0-1.84.67-2.91.71-1.12.04-2.28-.86-3.32-2.33-2.13-3-1.63-7.7.92-10.43 1.28-1.37 2.69-2.11 3.96-2.11 1.23 0 2.04.44 2.89.44.82 0 1.48-.44 3.01-.44 1.15 0 2.37.52 3.19 1.52-2.67 1.6-2.22 5.38.44 6.63-.52 1.41-1.22 2.82-2.11 4.04l.1.04zM12.03 5.4c.04-2.19 1.82-3.93 3.93-3.93.04 0 .07 0 .11 0-.15 2.19-1.93 3.93-4.04 3.93h-.11c.04 0 .07 0 .11 0z" />
                </svg>
                <span style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'white'
                }}>
                  {t('login.social.apple')}
                </span>
              </button>
            </div>

            {/* Sign Up Link */}
            <div style={{
              textAlign: 'center',
              width: '100%',
              marginTop: '8px',
            }}>
              <span style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                color: '#6B7280'
              }}>
                {t('login.noAccount')}{' '}
              </span>
              <button
                type="button"
                onClick={() => navigate('/auth/select-type')}
                disabled={isLoading}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: userTypeColor,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                {t('login.signUp')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        onSubmit={handleForgotPasswordSubmit}
        onSuccess={() => { }}
        isLoading={forgotPasswordLoading}
        error={forgotPasswordError}
      />

      <CheckEmailModal
        isOpen={showCheckEmailModal}
        onClose={() => {
          setShowForgotModal(false)
          setShowCheckEmailModal(false)
          setForgotPasswordError('')
          setResetEmail('')
        }}
        onResend={() => { }}
        email={resetEmail}
        isResending={isResendingEmail}
      />

      {/* Add spinning animation for loading */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          
          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            /* Hide mascot section on mobile */
            .login-mascot-section {
              display: none !important;
            }
            
            /* Make login form full width and centered */
            .login-form-section {
              width: 100% !important;
              padding: 40px 24px !important;
              align-items: flex-start !important;
              padding-top: 100px !important;
            }
            
            /* Center the form container */
            .login-form-container {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 auto !important;
            }
            
            /* Adjust language toggle position */
            .language-toggle-mobile {
              position: fixed !important;
              top: 16px !important;
              right: 16px !important;
              z-index: 1000 !important;
            }
            
            /* Adjust form elements on mobile */
            .login-form-section h3 {
              font-size: 24px !important;
            }
          }
        `}
      </style>
    </div>
  )
}

export default Login